import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Skeleton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';

import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DocumentScanner as DocumentScannerIcon,
  TaskAlt as TaskAltIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

import apiService from '../services/api';

import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { lineElementClasses } from '@mui/x-charts';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';

/** Зелёная (растительная) палитра для графиков */
const GREEN_PALETTE = ['#1b5e20', '#2e7d32', '#43a047', '#66bb6a', '#8bc34a', '#33691e', '#a5d6a7'];
const FAILED_COLOR = '#c62828';

const IntlAbsolute = new Intl.NumberFormat('ru-RU');
const IntlCompact = new Intl.NumberFormat('ru-RU', { notation: 'compact' });

/** Пресеты периода */
const PERIODS = [
  { value: '1d', label: '1 день', days: 1 },
  { value: '3d', label: '3 дня', days: 3 },
  { value: '7d', label: 'Неделя', days: 7 },
  { value: '30d', label: 'Месяц', days: 30 },
  { value: '365d', label: 'Год', days: 365 },
];

const formatPeriodDate = (date) =>
  date ? format(date, 'd MMM yyyy', { locale: ruLocale }) : '';

/** Пустые итоги за период */
const EMPTY_TOTALS = {
  registrations: 0, registrationsSuccessful: 0, registrationsFailed: 0,
  events: 0, uniqueUsers: 0, sessions: 0,
  scans: 0, scansSuccessful: 0, scansFailed: 0,
  tasks: 0, tasksSuccessful: 0, tasksFailed: 0,
  chat: 0, chatSuccessful: 0, chatFailed: 0, chatWithPhoto: 0, chatWithoutPhoto: 0,
};

/**
 * Суммируем дневную аналитику в итоги за период
 * @param {Array<Object>} data - дневная аналитика
 * @returns {Object} - итоги за период (структура EMPTY_TOTALS);
 * uniqueUsers - накопительное количество пользователей, берём последнее значение
 */
function aggregateAnalytics(data) {
  if (!data || data.length === 0) return { ...EMPTY_TOTALS };
  const totals = { ...EMPTY_TOTALS };
  data.forEach((day) => {
    totals.registrations += day.totalRegistrations || 0;
    totals.registrationsSuccessful += day.successfulRegistrations || 0;
    totals.registrationsFailed += day.failedRegistrations || 0;
    totals.events += day.totalEvents || 0;
    totals.sessions += day.totalSessions || 0;
    totals.scans += day.totalScans || 0;
    totals.scansSuccessful += day.successfulScans || 0;
    totals.scansFailed += day.failedScans || 0;
    totals.tasks += day.totalTasks || 0;
    totals.tasksSuccessful += day.successfulTasks || 0;
    totals.tasksFailed += day.failedTasks || 0;
    totals.chat += day.totalChatRequests || 0;
    totals.chatSuccessful += day.successfulChatRequests || 0;
    totals.chatFailed += day.failedChatRequests || 0;
    totals.chatWithPhoto += day.chatRequestsWithPhoto || 0;
    totals.chatWithoutPhoto += day.chatRequestsWithoutPhoto || 0;
  });
  totals.uniqueUsers = data[data.length - 1].uniqueUsers || 0;
  return totals;
}

/**
 * Среднее на пользователя
 * @param {number} value - суммарное значение
 * @param {number} users - количество пользователей
 * @returns {number} - среднее на пользователя, округлённое до сотых
 */
const perUser = (value, users) =>
  users > 0 ? Math.round((value / users) * 100) / 100 : 0;

/**
 * Статистическая карточка со спарклайном
 * @param {Object} props - свойства компонента
 * @param {string} props.title - заголовок карточки
 * @param {number} props.value - основное значение
 * @param {string} [props.subtitle] - подпись под значением
 * @param {Object} props.icon - иконка
 * @param {string} props.color - цветовая схема MUI
 * @param {Array<number>} [props.sparkData] - данные спарклайна
 * @param {boolean} props.loading - показывать скелетон загрузки
 * @param {boolean} props.isMobile - мобильная вёрстка
 */
const StatCard = ({ title, value, subtitle, icon, color, sparkData, loading, isMobile }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden'
    }}
  >
    <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            width: 36,
            height: 36
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
            {loading ? <Skeleton width={60} /> : IntlAbsolute.format(value)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {sparkData && sparkData.length > 1 && (
          <Box sx={{ width: 90, height: 44, flexShrink: 0 }}>
            <SparkLineChart
              data={sparkData}
              colors={['#43a047']}
              area
              showHighlight
              showTooltip
            />
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

/**
 * Маленькая карточка-график для сайдбара
 * @param {Object} props - свойства компонента
 * @param {string} props.title - заголовок карточки
 * @param {Object} props.children - содержимое (график)
 */
const SideChartCard = ({ title, children }) => (
  <Paper
    component="div"
    variant="outlined"
    sx={{ px: 1.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, px: 0.5 }}>
      {title}
    </Typography>
    {/** Растягиваем содержимое, чтобы график занимал всю высоту карточки */}
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
      {children}
    </Box>
  </Paper>
);

const NoData = ({ height = 140 }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height, color: 'text.secondary' }}>
    <Typography variant="body2">Нет данных за выбранный период</Typography>
  </Box>
);

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /** Период: по умолчанию неделя */
  const [period, setPeriod] = useState('7d');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());

  /** Данные */
  const [userStats, setUserStats] = useState({ totalUsers: 0, blockedUsers: 0 });
  const [recentUsers, setRecentUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState('registrations');

  /** Напоминания = задачи ухода/лечения: бэкенд-аналитика их не считает, считаем сами */
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(true);

  /** Загрузка пользователей */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiService.users.getAll(1, 5);
        if (response.data.success) {
          const users = response.data.data.users;
          setUserStats({
            totalUsers: response.data.data.total,
            blockedUsers: users.filter((user) => user.isBlocked).length,
          });
          setRecentUsers(users);
        }
      } catch (error) {
        console.error('Ошибка загрузки данных дашборда:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  /** Загрузка напоминаний (задач) - все, фильтрация по периоду на клиенте */
  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const response = await apiService.adminReminders.getReminders(1, 0);
        if (response.data.success) {
          setReminders(response.data.data.reminders || []);
        }
      } catch (error) {
        console.error('Ошибка загрузки напоминаний для дашборда:', error);
      } finally {
        setRemindersLoading(false);
      }
    };
    fetchReminders();
  }, []);

  /** Загрузка аналитики при смене периода */
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      try {
        const formatStart = startDate ? startDate.toISOString().split('T')[0] : '';
        const formatEnd = endDate ? endDate.toISOString().split('T')[0] : '';
        const response = await apiService.analytics.getAnalytics(formatStart, formatEnd);
        if (response.data.success) {
          setAnalyticsData(response.data.data);
        } else {
          setAnalyticsData([]);
        }
      } catch (error) {
        console.error('Ошибка загрузки аналитики:', error);
        setAnalyticsData([]);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    if (startDate && endDate) {
      fetchAnalytics();
    }
  }, [startDate, endDate]);

  /**
   * Переключение пресета периода
   * @param {Object} event - событие изменения ToggleButtonGroup
   * @param {string} newPeriod - выбранный пресет периода
   */
  const handlePeriodChange = (event, newPeriod) => {
    if (!newPeriod) return;
    setPeriod(newPeriod);
    const preset = PERIODS.find((p) => p.value === newPeriod);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (preset.days - 1));
    setStartDate(start);
    setEndDate(end);
  };

  /** Ручной выбор дат сбрасывает пресет */
  const handleStartDateChange = (date) => {
    setPeriod(null);
    setStartDate(date);
  };
  const handleEndDateChange = (date) => {
    setPeriod(null);
    setEndDate(date);
  };

  /**
   * Созданные задачи (напоминания) по дням выбранного периода.
   * Дата создания - createdAt; если его нет, берём дату самого напоминания.
   * Дни без задач заполняются нулями, чтобы график был непрерывным.
   */
  const tasksByDay = useMemo(() => {
    if (!startDate || !endDate) return [];
    /** uniqueUsers по датам из аналитики - для среднего на пользователя */
    const usersByDate = {};
    analyticsData.forEach((d) => {
      usersByDate[format(new Date(d.date), 'yyyy-MM-dd')] = d.uniqueUsers || 0;
    });
    const counts = {};
    reminders.forEach((r) => {
      const created = r.createdAt || r.date;
      if (!created) return;
      const key = format(new Date(created), 'yyyy-MM-dd');
      counts[key] = (counts[key] || 0) + 1;
    });
    const days = [];
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (cursor <= end) {
      const key = format(cursor, 'yyyy-MM-dd');
      const total = counts[key] || 0;
      days.push({ date: key, totalTasks: total, tasksPerUser: perUser(total, usersByDate[key] || 0) });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [reminders, analyticsData, startDate, endDate]);

  /** Итоги за период (задачи - из напоминаний, а не из бэкенд-аналитики) */
  const totals = useMemo(() => {
    const result = aggregateAnalytics(analyticsData);
    result.tasks = tasksByDay.reduce((sum, d) => sum + d.totalTasks, 0);
    return result;
  }, [analyticsData, tasksByDay]);

  /** Дневные серии для спарклайнов */
  const spark = useMemo(() => ({
    users: analyticsData.map((d) => d.uniqueUsers || 0),
    registrations: analyticsData.map((d) => d.totalRegistrations || 0),
    scans: analyticsData.map((d) => d.totalScans || 0),
    tasks: tasksByDay.map((d) => d.totalTasks),
    chat: analyticsData.map((d) => d.totalChatRequests || 0),
  }), [analyticsData, tasksByDay]);

  /** Сканы по дням недели (Пн–Вс) */
  const weekdayScans = useMemo(() => {
    const labels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const sums = [0, 0, 0, 0, 0, 0, 0];
    analyticsData.forEach((d) => {
      const day = new Date(d.date).getDay(); /** 0 = Вс */
      sums[(day + 6) % 7] += d.totalScans || 0;
    });
    return { labels, sums };
  }, [analyticsData]);

  /**
   * Высота основного графика подгоняется под высоту сайдбара,
   * но не ниже минимальной, чтобы оси и подписи не обрезались
   */
  const sidebarRef = useRef(null);
  const [mainChartHeight, setMainChartHeight] = useState(400);
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;
    const observer = new ResizeObserver((entries) => {
      const sidebarHeight = entries[0].contentRect.height;
      /** минус внутренние отступы левой колонки, селектор и зазоры */
      setMainChartHeight(Math.max(400, Math.round(sidebarHeight - 64)));
    });
    observer.observe(sidebar);
    return () => observer.disconnect();
  }, []);

  /** Доступные наборы данных (типы аналитики) */
  const packages = {
    registrations: {
      label: 'Регистрации пользователей',
      series: [
        { id: 'successful', label: 'Успешные', dataKey: 'successfulRegistrations', color: GREEN_PALETTE[2] },
        { id: 'failed', label: 'Неуспешные', dataKey: 'failedRegistrations', color: FAILED_COLOR },
        { id: 'total', label: 'Всего', dataKey: 'totalRegistrations', color: GREEN_PALETTE[0] },
      ]
    },
    uniqueUsers: {
      label: 'Уникальные пользователи',
      series: [
        { id: 'uniqueUsers', label: 'Уник. пользователи', dataKey: 'uniqueUsers', color: GREEN_PALETTE[4] },
      ]
    },
    confirmations: {
      label: 'Каналы подтверждений',
      series: [
        { id: 'confirmationsEmail', label: 'Email', dataKey: 'confirmationsEmail', color: GREEN_PALETTE[0] },
        { id: 'confirmationsSms', label: 'SMS', dataKey: 'confirmationsSms', color: GREEN_PALETTE[4] },
        { id: 'confirmationsTelegram', label: 'Telegram', dataKey: 'confirmationsTelegram', color: '#29a9eb' },
        { id: 'confirmationsMax', label: 'Max', dataKey: 'confirmationsMax', color: '#7c4dff' },
        { id: 'confirmationsWhatsapp', label: 'WhatsApp', dataKey: 'confirmationsWhatsapp', color: '#25d366' },
        { id: 'confirmationsGoogle', label: 'Google', dataKey: 'confirmationsGoogle', color: '#4285f4' },
        { id: 'confirmationsApple', label: 'Apple', dataKey: 'confirmationsApple', color: '#212121' },
        { id: 'confirmationsYandex', label: 'Яндекс', dataKey: 'confirmationsYandex', color: '#fc3f1d' },
      ]
    },
    eventsPerUser: {
      label: 'События на пользователя',
      series: [
        { id: 'events', label: 'Всего событий', dataKey: 'totalEvents', color: GREEN_PALETTE[1] },
        { id: 'eventsAvg', label: 'Событий на пользователя', dataKey: 'eventsPerUser', color: GREEN_PALETTE[4] },
      ]
    },
    activeSessions: {
      label: 'Активные сеансы (MAU)',
      series: [
        { id: 'uniqueUsers', label: 'Уник. пользователи', dataKey: 'uniqueUsers', color: GREEN_PALETTE[1] },
        { id: 'totalSessions', label: 'Всего сеансов', dataKey: 'totalSessions', color: GREEN_PALETTE[4] },
      ]
    },
    scans: {
      label: 'Сканирования',
      series: [
        { id: 'scansSuccessful', label: 'Успешные', dataKey: 'successfulScans', color: GREEN_PALETTE[2] },
        { id: 'scansFailed', label: 'Неуспешные', dataKey: 'failedScans', color: FAILED_COLOR },
        { id: 'scansTotal', label: 'Всего', dataKey: 'totalScans', color: GREEN_PALETTE[0] },
        { id: 'scansAvg', label: 'Среднее на пользователя', dataKey: 'scansPerUser', color: GREEN_PALETTE[4] },
      ]
    },
    tasks: {
      label: 'Созданные задачи',
      series: [
        { id: 'tasksTotal', label: 'Всего', dataKey: 'totalTasks', color: GREEN_PALETTE[0] },
        { id: 'tasksAvg', label: 'Среднее на пользователя', dataKey: 'tasksPerUser', color: GREEN_PALETTE[4] },
      ]
    },
    chatRequests: {
      label: 'Запросы в чат',
      series: [
        { id: 'chatSuccessful', label: 'Успешные', dataKey: 'successfulChatRequests', color: GREEN_PALETTE[2] },
        { id: 'chatFailed', label: 'Неуспешные', dataKey: 'failedChatRequests', color: FAILED_COLOR },
        { id: 'chatTotal', label: 'Всего', dataKey: 'totalChatRequests', color: GREEN_PALETTE[0] },
        { id: 'chatAvg', label: 'Среднее на пользователя', dataKey: 'chatRequestsPerUser', color: GREEN_PALETTE[4] },
        { id: 'chatWithPhoto', label: 'С фото', dataKey: 'chatRequestsWithPhoto', color: GREEN_PALETTE[5] },
        { id: 'chatWithoutPhoto', label: 'Без фото', dataKey: 'chatRequestsWithoutPhoto', color: GREEN_PALETTE[6] },
      ]
    }
  };

  const selectedData = packages[selectedPackage];

  /** Для задач источник - напоминания (tasksByDay), для остальных - бэкенд-аналитика */
  const chartDataset = selectedPackage === 'tasks' ? tasksByDay : analyticsData;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ruLocale}>
      <Box>
        {/** Заголовок и период */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            mb: { xs: 2, sm: 3 },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' }
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Дашборд
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Данные за период: {formatPeriodDate(startDate)} - {formatPeriodDate(endDate)}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              color="primary"
              value={period}
              exclusive
              onChange={handlePeriodChange}
            >
              {PERIODS.map((p) => (
                <ToggleButton key={p.value} value={p.value} sx={{ px: 1.5 }}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1}>
              <DatePicker
                label="От"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
              />
              <DatePicker
                label="До"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
              />
            </Stack>
          </Stack>
        </Stack>

        {/** Статистические карточки */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Всего пользователей"
              value={userStats.totalUsers}
              icon={<PeopleIcon fontSize="small" />}
              color="primary"
              sparkData={spark.users}
              loading={usersLoading}
              isMobile={isMobile}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Новые пользователи"
              value={totals.registrations}
              subtitle={`в среднем ${perUser(totals.registrations, totals.uniqueUsers)} на польз.`}
              icon={<PersonAddIcon fontSize="small" />}
              color="success"
              sparkData={spark.registrations}
              loading={analyticsLoading}
              isMobile={isMobile}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Заблокированные"
              value={userStats.blockedUsers}
              icon={<BlockIcon fontSize="small" />}
              color="error"
              loading={usersLoading}
              isMobile={isMobile}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Сканирования"
              value={totals.scans}
              subtitle={`в среднем ${perUser(totals.scans, totals.uniqueUsers)} на польз.`}
              icon={<DocumentScannerIcon fontSize="small" />}
              color="primary"
              sparkData={spark.scans}
              loading={analyticsLoading}
              isMobile={isMobile}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Новые задачи"
              value={totals.tasks}
              subtitle={`в среднем ${perUser(totals.tasks, totals.uniqueUsers)} на польз.`}
              icon={<TaskAltIcon fontSize="small" />}
              color="secondary"
              sparkData={spark.tasks}
              loading={remindersLoading}
              isMobile={isMobile}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <StatCard
              title="Запросы в чат"
              value={totals.chat}
              subtitle={`в среднем ${perUser(totals.chat, totals.uniqueUsers)} на польз.`}
              icon={<ChatIcon fontSize="small" />}
              color="secondary"
              sparkData={spark.chat}
              loading={analyticsLoading}
              isMobile={isMobile}
            />
          </Grid>
        </Grid>

        {/** Основной график + сайдбар */}
        <Paper
          component="div"
          variant="outlined"
          sx={{
            mb: 4,
            mx: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            p: 1,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minHeight: 'fit-content' }}>
            {/** Основной график */}
            <Stack
              direction="column"
              spacing={1}
              sx={{
                flexBasis: { xs: '100%', md: '65%' },
                minWidth: 0,
                pr: { md: 1, xs: 0 },
                p: 1,
                minHeight: '100%',
              }}
            >
              <Select
                size="small"
                value={selectedPackage}
                onChange={(event) => setSelectedPackage(event.target.value)}
                sx={{ alignSelf: 'flex-start', minWidth: 240 }}
              >
                {Object.entries(packages).map(([key, pkg]) => (
                  <MenuItem key={key} value={key}>
                    {pkg.label}
                  </MenuItem>
                ))}
              </Select>

              <Box sx={{ height: mainChartHeight, minHeight: 224, display: 'flex', flexDirection: 'column' }}>
                {(selectedPackage === 'tasks' ? remindersLoading : analyticsLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: mainChartHeight, flex: 1 }}>
                    <CircularProgress color="primary" />
                  </Box>
                ) : chartDataset.length > 0 ? (
                  <LineChart
                    skipAnimation
                    dataset={chartDataset}
                    series={selectedData.series.map((s) => ({
                      id: s.id,
                      dataKey: s.dataKey,
                      area: true,
                      curve: 'linear',
                      label: s.label,
                      showMark: false,
                      valueFormatter: (value) => (value !== null ? IntlAbsolute.format(value) : null),
                      color: s.color,
                    }))}
                    xAxis={[
                      {
                        dataKey: 'date',
                        scaleType: 'band',
                        domainLimit: 'strict',
                        zoom: true,
                        height: 30,
                        tickNumber: 6,
                      },
                    ]}
                    yAxis={[
                      {
                        min: 0,
                        zoom: true,
                        valueFormatter: IntlCompact.format,
                      },
                    ]}
                    height={mainChartHeight}
                    margin={{ left: 50 }}
                    sx={{
                      [`& .${lineElementClasses.line}`]: {
                        strokeWidth: 1.5,
                      },
                      flex: 1,
                      padding: '0.5rem 0',
                    }}
                  />
                ) : (
                  <NoData height={mainChartHeight} />
                )}
              </Box>
            </Stack>

            {/** Сайдбар с разбивками */}
            <Stack
              ref={sidebarRef}
              direction="column"
              spacing={1}
              sx={{
                flexBasis: { xs: '100%', md: '35%' },
                minWidth: 0,
                minHeight: '100%',
                height: '100%'
              }}
            >
              <SideChartCard title="Запросы в чат: с фото / без фото">
                {analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', height: 140, alignItems: 'center' }}>
                    <CircularProgress size={28} color="primary" />
                  </Box>
                ) : totals.chat > 0 ? (
                  <PieChart
                    series={[
                      {
                        startAngle: -90,
                        endAngle: 90,
                        innerRadius: '130%',
                        outerRadius: '185%',
                        cy: '100%',
                        data: [
                          { label: 'С фото', value: totals.chatWithPhoto, color: GREEN_PALETTE[1] },
                          { label: 'Без фото', value: totals.chatWithoutPhoto, color: GREEN_PALETTE[4] },
                        ],
                      },
                    ]}
                    height={160}
                    margin={{ top: 0, bottom: 56, left: 0, right: 0 }}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'center' },
                      },
                    }}
                  />
                ) : (
                  <NoData height={160} />
                )}
              </SideChartCard>

              <SideChartCard title="Сканирования по дням недели">
                {analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', height: 140, alignItems: 'center' }}>
                    <CircularProgress size={28} color="primary" />
                  </Box>
                ) : weekdayScans.sums.some((v) => v > 0) ? (
                  <BarChart
                    dataset={weekdayScans.labels.map((label, i) => ({ day: label, value: weekdayScans.sums[i] }))}
                    colors={[GREEN_PALETTE[2]]}
                    series={[{ dataKey: 'value' }]}
                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                    height={160}
                    borderRadius={4}
                    xAxis={[{ dataKey: 'day', scaleType: 'band', disableTicks: true }]}
                    yAxis={[{ position: 'left' }]}
                  />
                ) : (
                  <NoData height={160} />
                )}
              </SideChartCard>
            </Stack>
          </Stack>
        </Paper>

        {/** Последние пользователи */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Последние пользователи
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {usersLoading ? (
            Array.from(new Array(5)).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', my: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ width: '100%' }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Box>
            ))
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {recentUsers.length > 0 ? (
                recentUsers.map((user) => (
                  <ListItem key={user._id} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: user.isBlocked ? 'error.main' : 'primary.main' }}>
                        {user.authMethod === 'email' ? <EmailIcon /> : <PhoneIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.email || user.phone}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {user.authMethod === 'email' ? 'Email' : 'Телефон'}
                          </Typography>
                          {' - '}
                          {user.isBlocked ? 'Заблокирован' : 'Активен'}
                          {' • '}
                          {new Date(user.createdAt).toLocaleDateString('ru-RU')}
                        </>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Нет данных о пользователях
                </Typography>
              )}
            </List>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
